import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Search, Heart, ShoppingBag, MapPin, User, Menu, ChevronDown, Phone, X, LogOut,
} from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { CURRENCIES } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Spiritual", to: "/category/spiritual" },
  { label: "Mala", to: "/category/mala" },
  { label: "Vigrah", to: "/category/vigrah" },
  { label: "Pooja Samagri", to: "/category/pooja-samagri" },
  { label: "Bhagwan Vastra", to: "/category/bhagwan-vastra" },
  { label: "Books", to: "/category/books" },
  { label: "Raw Material", to: "/category/raw-material" },
  { label: "B2B Wholesale", to: "/b2b", highlight: true },
  { label: "Vendors", to: "/vendors" },
  { label: "Offers", to: "/shop" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
      <div className="h-10 w-10 rounded-xl bg-secondary text-white grid place-items-center font-serif font-bold text-lg shadow-sm">
        Sv
      </div>
      <div className="leading-none">
        <div className="font-serif font-bold text-[17px] text-secondary">Shri Vrindavan Shop</div>
        <div className="text-[10px] text-stone-500 tracking-wide">Braj Ki Bhakti, Aapke Ghar Tak</div>
      </div>
    </Link>
  );
}

function SearchBar({ id = "header-search-input" }) {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
  };
  return (
    <form onSubmit={submit} className="flex-1 flex items-center rounded-full border border-border bg-white overflow-hidden focus-within:ring-2 focus-within:ring-primary/30">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-testid={id}
        placeholder="Search Mala, Vigrah, Pooja Samagri, Books..."
        className="flex-1 px-4 py-2.5 text-sm bg-transparent outline-none"
      />
      <button type="submit" aria-label="Search" data-testid="header-search-submit" className="h-full px-4 bg-primary text-primary-foreground flex items-center">
        <Search size={18} />
      </button>
    </form>
  );
}

function CurrencySelect() {
  const { currency, setCurrency } = useStore();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="header-currency-select" className="flex items-center gap-1 text-xs font-medium hover:text-primary">
          {currency} <ChevronDown size={13} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-72 overflow-auto">
        <DropdownMenuLabel className="text-[11px]">Display currency</DropdownMenuLabel>
        {CURRENCIES.map((c) => (
          <DropdownMenuItem
            key={c.code}
            data-testid={`currency-option-${c.code}`}
            onClick={() => {
              setCurrency(c.code);
              if (c.code !== "INR")
                toast("Prices shown in INR. Live conversion is coming soon.");
            }}
          >
            <span className="font-medium mr-2">{c.symbol}</span>
            {c.code} — {c.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function B2BToggle() {
  const { b2bMode, setB2bMode } = useStore();
  return (
    <div className="inline-flex rounded-full border border-white/25 p-0.5 text-[11px] font-semibold">
      <button
        data-testid="mode-b2c-btn"
        onClick={() => setB2bMode(false)}
        className={`px-3 py-1 rounded-full transition-colors ${!b2bMode ? "bg-white text-secondary" : "text-white/80"}`}
      >
        B2C Retail
      </button>
      <button
        data-testid="mode-b2b-btn"
        onClick={() => setB2bMode(true)}
        className={`px-3 py-1 rounded-full transition-colors ${b2bMode ? "bg-primary text-primary-foreground" : "text-white/80"}`}
      >
        B2B Wholesale
      </button>
    </div>
  );
}

function AccountMenu() {
  const { user, logout } = useStore();
  const navigate = useNavigate();
  if (!user) {
    return (
      <Link to="/login" data-testid="header-login-btn" className="flex items-center gap-1.5 text-sm font-medium hover:text-primary">
        <User size={18} /> <span className="hidden lg:inline">Login</span>
      </Link>
    );
  }
  const links = [
    { label: "My Account", to: "/account" },
    { label: "Orders", to: "/orders" },
    { label: "Wishlist", to: "/wishlist" },
    { label: "Wallet", to: "/wallet" },
    { label: "Referral", to: "/referral" },
  ];
  if (user.role === "vendor") links.push({ label: "Vendor Dashboard", to: "/vendor/dashboard" });
  if (["admin", "staff"].includes(user.role)) links.push({ label: "Admin Panel", to: "/admin" });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="header-account-btn" className="flex items-center gap-1.5 text-sm font-medium hover:text-primary">
          <User size={18} /> <span className="hidden lg:inline max-w-[90px] truncate">{user.name?.split(" ")[0]}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel>
          <div className="text-sm font-semibold">{user.name}</div>
          <div className="text-[11px] text-stone-500 capitalize">{user.role.replace("_", " ")}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {links.map((l) => (
          <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} data-testid={`account-menu-${l.to.replace(/\//g, "-")}`}>
            {l.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} data-testid="header-logout-btn" className="text-red-600">
          <LogOut size={14} className="mr-2" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Header() {
  const { cartCount, wishlist } = useStore();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-secondary text-white/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-xs">
          <div className="flex items-center gap-4">
            <B2BToggle />
            <span className="hidden md:inline text-white/70">Pan India Delivery • Trusted Vendors</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:+910000000000" className="hidden sm:flex items-center gap-1 text-white/80 hover:text-white">
              <Phone size={12} /> Helpline
            </a>
            <CurrencySelect />
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-trigger" aria-label="Menu" className="p-1.5">
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[85vw] max-w-sm p-0 overflow-auto">
                <SheetHeader className="p-4 border-b border-border">
                  <SheetTitle className="text-left"><Logo /></SheetTitle>
                </SheetHeader>
                <div className="p-4">
                  <div className="mb-4"><B2BToggle /></div>
                  <nav className="flex flex-col">
                    {NAV.map((n) => (
                      <Link key={n.label} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`} className="py-2.5 border-b border-border text-sm font-medium text-stone-700">
                        {n.label}
                      </Link>
                    ))}
                  </nav>
                  <div className="mt-4 flex flex-col gap-2">
                    <Link to="/vendor/register" className="text-sm font-semibold text-secondary">Sell on Shri Vrindavan Shop →</Link>
                    <Link to="/account" className="text-sm text-stone-600">My Account</Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <Logo />

          <div className="hidden md:flex flex-1 max-w-2xl">
            <SearchBar />
          </div>

          <div className="hidden lg:flex items-center gap-1 text-sm text-stone-600">
            <MapPin size={16} className="text-primary" />
            <div className="leading-tight">
              <div className="text-[10px] text-stone-400">Deliver to</div>
              <div className="font-medium">Pan India</div>
            </div>
          </div>

          <div className="flex items-center gap-4 ml-auto">
            <Link to="/wishlist" data-testid="header-wishlist-btn" className="relative hover:text-primary hidden sm:block">
              <Heart size={20} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 h-4 min-w-4 px-1 rounded-full bg-secondary text-white text-[10px] grid place-items-center">
                  {wishlist.length}
                </span>
              )}
            </Link>
            <Link to="/cart" data-testid="header-cart-btn" className="relative hover:text-primary">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span data-testid="cart-badge" className="absolute -top-2 -right-2 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] grid place-items-center">
                  {cartCount}
                </span>
              )}
            </Link>
            <AccountMenu />
          </div>
        </div>

        {/* Mobile search */}
        <div className="md:hidden px-4 pb-3">
          <SearchBar id="mobile-search-input" />
        </div>

        {/* Desktop category nav */}
        <nav className="hidden lg:block border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex items-center gap-1 h-11 overflow-x-auto hide-scrollbar">
              {NAV.map((n) => (
                <li key={n.label}>
                  <Link
                    to={n.to}
                    data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                      n.highlight ? "text-secondary font-semibold" : "text-stone-700 hover:text-primary hover:bg-accent/60"
                    }`}
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </div>
    </header>
  );
}
